import {Box, Tab, Tabs} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {eventSettingsTabs} from './helpers';
import {EventTableTabsStyle} from './EventSettingTabs.style';
import EventSettingForm from './EventSettingForm';
import {Formik} from 'formik';
import {
  CreateEventFormValues,
  initialValues,
  validationSchema,
} from '@/components/Organization/Dashboard/CreateNewEvent/formConfiguration';
import TablesAndTicketsForm from './TablesAndTicketsForm';
import EventWebPageForm from './EventWebPageForm';
import {useSelector} from 'react-redux';
import {
  getCurrentEventSelector,
  getEvents,
  getEventSponsorCategories,
  getSponsorCategoriesSelector,
  updateEvent,
} from '@/store/organization-event';
import Loader from '@/shared/components/Loader';
import SponsorSettingsList from './SponsorSettingsList';
import {useAppDispatch} from '@/store/hooks';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import dayjs from 'dayjs';
import {CANCEL, MERIDIEM_HOUR_FORMAT, MM_DD_YYYY_SLASH, OUTLINED, SAVE_CHANGES} from '@/utils/constants';
import {OrganizationEventService} from '@/utils/services';
import {EVENT_IMAGE, ITeamRole} from '@/utils/enums';
import utc from 'dayjs/plugin/utc';
import Button from '@/shared/components/Button';
import {IOrganizationEventRequest} from '@/utils/types';
import {getNewEventValues} from '@/components/Organization/Dashboard/CreateNewEvent/helpers';
import {getEventImage} from '@/components/Organization/Dashboard/CreateNewEvent';
import {sendPresignedImage} from '../Profile/General/ChangePhotoModal';
import {useGetOrganizationId} from '@/utils/hooks';
import {getTeamSelector} from '@/store/organization';
import { resolveCategoryId } from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/EventSponsors/AddSponsorModalForm/helpers';

dayjs.extend(utc);

const EventSettings = () => {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<string>(eventSettingsTabs[0]);

  const event = useSelector(getCurrentEventSelector);
  const organizationId = useGetOrganizationId();
  const team = useSelector(getTeamSelector);
  const sponsorCategories = useSelector(getSponsorCategoriesSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  useEffect(() => {
    const getSponsorCategories = async () => {
      try {
        await dispatch(getEventSponsorCategories(organizationId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    getSponsorCategories();
  }, []);

  const formikInitialValues = useMemo(() => {
    const initialDataValues = event
      ? {
          name: event.name,
          logo: event.image,
          prizeLogo: event.raffleInfo.raffleImage ?? '',
          webpageLogo: event.image,
          location: event.location,
          organizationId: event.organizationId,
          raffleTires: event.raffleInfo.tickets.map((ticket) => ({
            id: ticket.id,
            numberOfTickets: ticket.qty,
            price: ticket.price,
          })),
          customizeWebpage: event.customizeWebpage,
          description: event.description,
          endAuctionDate: event.isOnlineAuction
            ? dayjs.utc(event.endAuctionData).format(MM_DD_YYYY_SLASH)
            : dayjs().format(MM_DD_YYYY_SLASH),
          endAuctionTime: event.isOnlineAuction
            ? dayjs.utc(event.endAuctionData).format(MERIDIEM_HOUR_FORMAT)
            : '12:00 am',
          startDate: dayjs.utc(event.startData).format(MM_DD_YYYY_SLASH),
          startTime: dayjs.utc(event.startData).format(MERIDIEM_HOUR_FORMAT),
          endDate: dayjs.utc(event.endData).format(MM_DD_YYYY_SLASH),
          endTime: dayjs.utc(event.endData).format(MERIDIEM_HOUR_FORMAT),
          endRaffleDate: event.isRaffle
            ? dayjs.utc(event.raffleInfo.endRaffleDate).format(MM_DD_YYYY_SLASH)
            : dayjs().format(MM_DD_YYYY_SLASH),
          endRaffleTime: event.isRaffle
            ? dayjs.utc(event.raffleInfo.endRaffleDate).format(MERIDIEM_HOUR_FORMAT)
            : '12:00 am',
          id: event.id,
          isFundTheMission: event.isFundTheMission,
          isGoalAmount: event.isGoalAmount,
          goalAmount: event.goalAmount || null,
          isOnlineAuction: event.isOnlineAuction,
          isRaffle: event.isRaffle,
          primaryColor: event.primaryColor || '',
          quantity: event.raffleInfo.generalQty ? event.raffleInfo.generalQty : null,
          raffleDescription: event.raffleInfo?.raffleDescription || '',
          sponsors: event.sponsors,
          startAuctionDate: event.isOnlineAuction
            ? dayjs.utc(event.startAuctionData).format(MM_DD_YYYY_SLASH)
            : dayjs().format(MM_DD_YYYY_SLASH),
          startAuctionTime: event.isOnlineAuction
            ? dayjs.utc(event.startAuctionData).format(MERIDIEM_HOUR_FORMAT)
            : '12:00 am',
          startRaffleDate: event.isRaffle
            ? dayjs.utc(event.raffleInfo.startRaffleDate).format(MM_DD_YYYY_SLASH)
            : dayjs().format(MM_DD_YYYY_SLASH),
          startRaffleTime: event.isRaffle
            ? dayjs.utc(event.raffleInfo.startRaffleDate).format(MERIDIEM_HOUR_FORMAT)
            : '12:00 am',
          tableInfos: event.tableInfos,
          ticketsSalesEndDate: dayjs.utc(event.ticketsSalesEnd).format(MM_DD_YYYY_SLASH),
          ticketsSalesEndTime: dayjs.utc(event.ticketsSalesEnd).format(MERIDIEM_HOUR_FORMAT),
          ticketsSalesStartDate: dayjs.utc(event.ticketsSalesStart).format(MM_DD_YYYY_SLASH),
          ticketsSalesStartTime: dayjs.utc(event.ticketsSalesStart).format(MERIDIEM_HOUR_FORMAT),
          toDoList: event.toDoList,
        }
      : initialValues;

    return initialDataValues;
  }, [event]);

const updateEventHandler = async (values: CreateEventFormValues) => {
  const image =
    values.logo instanceof File
      ? await getEventImage(values.logo as File, EVENT_IMAGE.EVENT)
      : (values.logo.split('/')[4]?.split('?')[0] as string);

  const raffleImage =
    values.prizeLogo instanceof File
      ? await getEventImage(values.prizeLogo as File, EVENT_IMAGE.RAFFLE)
      : values.prizeLogo
      ? (values.prizeLogo.split('/')[4]?.split('?')[0] as string)
      : null;

  const existingCategories = sponsorCategories || [];
  const categoriesCache = new Map<string, number>();

  const uniqueCategoryNames = [
    ...new Set(values.sponsors.map((s) => s?.category?.name?.trim()).filter((name): name is string => Boolean(name))),
  ];
  for (const categoryName of uniqueCategoryNames) {
    // eslint-disable-next-line no-await-in-loop
    await resolveCategoryId(organizationId, categoryName, existingCategories, categoriesCache);
  }

  const allSponsorsData = await Promise.all(
    values.sponsors.map(async (sponsor) => {
      const categoryId = await resolveCategoryId(organizationId, sponsor?.category?.name || '', existingCategories, categoriesCache);

      if (sponsor.logo instanceof File) {
        const presignedUrl = await OrganizationEventService.presignedImage(EVENT_IMAGE.SPONSOR);
        await sendPresignedImage(presignedUrl.data.url, sponsor.logo);

        return {
          name: sponsor.name,
          logo: presignedUrl.data.key.split('/').pop() as string,
          categoryId,
          sortOrder: sponsor.sortOrder,
        };
      }

      return {
        name: sponsor.name,
        logo: sponsor.logo?.split('/').pop()?.split('?')[0] as string,
        categoryId,
        sortOrder: sponsor.sortOrder,
      };
    })
  );

  const newEventValues: IOrganizationEventRequest = getNewEventValues(values, raffleImage);

  try {
    await dispatch(
      updateEvent({
        id: event!.id,
        reqBody: {
          ...newEventValues,
          image,
          sponsors: allSponsorsData,
          raffleInfo: {
            ...newEventValues.raffleInfo,
            raffleImage: raffleImage ?? '',
          },
        },
      })
    ).unwrap();
    await dispatch(getEvents(organizationId)).unwrap();

    toast.success('Event updated successfully');
  } catch (error) {
    toast.error(getErrorMessage(error));
  }
};

  const changeTab = (newTab: string) => () => {
    setActiveTab(newTab);
  };

  const tabData = useMemo(() => {
    switch (activeTab) {
      case eventSettingsTabs[0]:
        return (
          <Box>
            <EventSettingForm />
            <SponsorSettingsList />
          </Box>
        );

      case eventSettingsTabs[1]:
        return <TablesAndTicketsForm />;

      case eventSettingsTabs[2]:
        return <EventWebPageForm />;

      default:
        break;
    }
  }, [activeTab]);

  return (
    <Box sx={EventTableTabsStyle.containerWrapper}>
      {!event ? (
        <Loader sx={EventTableTabsStyle.loader} size={50} />
      ) : (
        <Box sx={EventTableTabsStyle.container}>
          <>
            <Box sx={EventTableTabsStyle.wrapper}>
              <Tabs value={activeTab} sx={EventTableTabsStyle.tabs}>
                {eventSettingsTabs.map((tab: string) => (
                  <Tab
                    onClick={changeTab(tab)}
                    key={tab}
                    value={tab}
                    label={<Box>{tab}</Box>}
                    sx={EventTableTabsStyle.tab}
                    disableRipple
                  />
                ))}
              </Tabs>
            </Box>

            <Formik
              initialValues={formikInitialValues}
              enableReinitialize
              onSubmit={updateEventHandler}
              validationSchema={validationSchema(eventSettingsTabs.indexOf(activeTab))}
              validateOnChange={true}
            >
              {({resetForm, submitForm}) => (
                <>
                  {tabData}

                  <Box sx={EventTableTabsStyle.buttonsWrapper}>
                    <Button title={CANCEL} variant={OUTLINED} onClick={() => resetForm()} disabled={isStandartMember} />
                    <Button title={SAVE_CHANGES} onClick={() => submitForm()} disabled={isStandartMember} />
                  </Box>
                </>
              )}
            </Formik>
          </>
        </Box>
      )}
    </Box>
  );
};

export default EventSettings;
